import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, Button, Card, ErrorNotice, Icon, StatusPill } from '@hive/ui';
import { api, errorMessage, type StreamProbe, type StreamSetup } from '../api/client';
import { useLoad, useNow } from '../api/hooks';
import { Shell } from '../components/Shell';
import { CONTRIBUTION, StreamFields, streamForm, type StreamFormState } from '../components/StreamForm';
import { TestPlayer } from '../components/TestPlayer';
import { ago } from '../format';
import { STREAM_STATUS } from './StreamsPage';
import { AuditFeed } from './UsersPage';

/** One stream: watch it, read its probe, change it, publish it. */
export function StreamPage() {
  const { streamId = '' } = useParams();
  const now = useNow(15_000);
  const loaded = useLoad(async () => (await api.GET('/v1/setup/streams/{streamId}', { params: { path: { streamId } } })).data, [streamId]);
  const history = useLoad(async () => (await api.GET('/v1/audit', { params: { query: { targetType: 'stream', targetId: streamId, limit: 30 } } })).data, [streamId]);
  const [stream, setStream] = useState<StreamSetup | null>(null);
  const [form, setForm] = useState<StreamFormState | null>(null);
  const [which, setWhich] = useState<'main' | 'backup'>('main');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (loaded.data) {
      setStream(loaded.data);
      setForm(streamForm(loaded.data));
    }
  }, [loaded.data]);

  const apply = (s: StreamSetup) => {
    setStream(s);
    setForm(streamForm(s));
    void history.reload();
  };

  async function run(label: string, action: () => Promise<{ data?: StreamSetup; error?: unknown }>) {
    setBusy(label);
    setError(null);
    setNotice(null);
    const { data, error: err } = await action();
    setBusy(null);
    if (!data) return setError(errorMessage(err));
    apply(data);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!form || !stream) return;
    await run('save', () =>
      api.PATCH('/v1/setup/streams/{streamId}', {
        params: { path: { streamId } },
        body: {
          name: form.name.trim(),
          code: form.code.trim(),
          description: form.description.trim(),
          streamUrl: form.streamUrl.trim(),
          backupUrl: form.backupUrl.trim(),
          streamType: form.streamType,
          contribution: form.contribution,
          adRights: form.adRights,
          scte35: form.scte35,
        },
      }),
    );
    setNotice(stream.status === 'published' && (form.streamUrl.trim() !== stream.streamUrl || form.backupUrl.trim() !== (stream.backupUrl ?? '')) ? 'Saved. Screens on this stream have been sent the new address.' : 'Saved.');
  }

  const s = stream;
  const probe = which === 'backup' ? s?.backupProbe : s?.probe;
  const playUrl = which === 'backup' ? s?.backupUrl : s?.streamUrl;

  return (
    <Shell network={null} live>
      <main className="mc-page">
        <Breadcrumbs items={[{ label: 'Streams', href: '/streams' }, { label: s?.name ?? '…' }]} renderLink={(href, label) => <Link to={href}>{label}</Link>} />
        {loaded.error && <p className="mc-note mc-note--bad">Could not load this stream.</p>}
        {s && form && (
          <>
            <div className="mc-pagehead">
              <div className="mc-pagehead__left">
                <h1 className="mc-h1">{s.name}</h1>
                <StatusPill status={STREAM_STATUS[s.status].status}>{STREAM_STATUS[s.status].label}</StatusPill>
                {s.code && <span className="mc-pill-soft">{s.code}</span>}
                <span className="mc-small hv-muted">
                  {CONTRIBUTION[s.contribution].label} · on {s.screensPlaying} screen{s.screensPlaying === 1 ? '' : 's'} ·{' '}
                  {s.contribution === 'channel'
                    ? `${s.venuesWithRights} venue${s.venuesWithRights === 1 ? '' : 's'} with rights`
                    : `${s.fixtures} fixture${s.fixtures === 1 ? '' : 's'} and loops`}
                </span>
              </div>
              <div className="mc-pagehead__actions">
                <Button onClick={() => run('probe', () => api.POST('/v1/setup/streams/{streamId}/probe', { params: { path: { streamId } } }))} disabled={!!busy}>
                  <Icon name="restart" size={16} />
                  {busy === 'probe' ? 'Probing…' : 'Probe now'}
                </Button>
                {s.status === 'published' ? (
                  <Button onClick={() => run('unpublish', () => api.POST('/v1/setup/streams/{streamId}/unpublish', { params: { path: { streamId } } }))} disabled={!!busy}>
                    Unpublish
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={() => run('publish', () => api.POST('/v1/setup/streams/{streamId}/publish', { params: { path: { streamId } } }))}
                    disabled={!!busy || !s.probe?.ok}
                    title={s.probe?.ok ? undefined : 'Probe it cleanly first'}
                  >
                    Publish to venues
                  </Button>
                )}
              </div>
            </div>
            {error && <ErrorNotice title={error} />}
            {offerNote(s) && <p className="mc-note mc-note--info">{offerNote(s)}</p>}

            <div className="mc-setup-grid">
              <div className="mc-col">
                <Card padded className="mc-side-card">
                  <div className="mc-row-between">
                    <span className="mc-section-title">Test player</span>
                    {s.backupUrl && (
                      <div className="mc-tabs" role="tablist" aria-label="Address">
                        {(['main', 'backup'] as const).map((w) => (
                          <button key={w} type="button" role="tab" aria-selected={which === w} className={`mc-tab${which === w ? ' mc-tab--on' : ''}`} onClick={() => setWhich(w)}>
                            {w === 'main' ? 'Main' : 'Backup'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {playUrl && <TestPlayer key={`${playUrl}-${s.streamType}`} url={playUrl} type={s.streamType} />}
                  <p className="mc-tiny hv-muted mc-wrap">{playUrl}</p>
                </Card>

                <Card padded className="mc-side-card">
                  <span className="mc-section-title">Probe {which === 'backup' ? 'of the backup' : ''}</span>
                  <ProbeReport probe={probe} now={now} />
                </Card>
              </div>

              <div className="mc-col">
                <Card padded className="mc-side-card">
                  <span className="mc-section-title">Details</span>
                  <form onSubmit={save} className="mc-form">
                    <StreamFields form={form} onChange={(f) => (setForm(f), setNotice(null))} />
                    <div className="mc-row-between">
                      <span className="mc-small hv-muted">{notice ?? (s.status === 'published' ? 'A new address reaches screens on this stream straight away.' : '')}</span>
                      <Button variant="primary" type="submit" disabled={!!busy}>
                        Save
                      </Button>
                    </div>
                  </form>
                </Card>
                <Card padded className="mc-side-card">
                  <span className="mc-section-title">
                    <Icon name="history" size={16} />
                    &nbsp;History
                  </span>
                  <AuditFeed events={history.data ?? []} now={now} />
                </Card>
              </div>
            </div>
          </>
        )}
      </main>
    </Shell>
  );
}

function ProbeReport({ probe, now }: { probe?: StreamProbe; now: number }) {
  if (!probe) return <p className="mc-small hv-muted">Not probed yet. Probe it to read the manifest.</p>;
  if (!probe.ok) return <p className="mc-note mc-note--bad">{probe.error ?? 'The probe failed'} · {ago(probe.at, now)} ago</p>;
  return (
    <div className="mc-form">
      <div className="mc-kv">
        <span className="hv-muted">Checked</span>
        <span>
          {ago(probe.at, now)} ago · answered in {probe.durationMs} ms
        </span>
        <span className="hv-muted">Kind</span>
        <span>{probe.live ? 'Live' : 'On demand'}</span>
        <span className="hv-muted">Audio</span>
        <span>{probe.audioLanguages?.length ? probe.audioLanguages.join(', ') : 'No languages declared'}</span>
        <span className="hv-muted">Subtitles</span>
        <span>{probe.subtitleLanguages?.length ? probe.subtitleLanguages.join(', ') : 'None'}</span>
        <span className="hv-muted">Ad markers</span>
        <span>{probe.scte35?.length ? probe.scte35.join(', ') : 'None seen'}</span>
      </div>
      <table className="mc-table mc-table--compact">
        <thead>
          <tr>
            <th scope="col">Rendition</th>
            <th scope="col">Bitrate</th>
            <th scope="col">Codecs</th>
          </tr>
        </thead>
        <tbody>
          {(probe.renditions ?? []).map((r, i) => (
            <tr key={i}>
              <td>{r.width && r.height ? `${r.width}×${r.height}` : '—'}</td>
              <td>{(r.bandwidthKbps / 1000).toFixed(2)} Mbps</td>
              <td className="mc-small hv-muted">{r.codecs ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {probe.warnings?.map((w) => (
        <p key={w} className="mc-note mc-note--info">
          {w}
        </p>
      ))}
    </div>
  );
}

/** Why venues are not offered this stream yet, when that is the case. */
function offerNote(s: StreamSetup): string | null {
  if (s.status !== 'published') return null;
  if (s.contribution === 'channel') {
    return s.venuesWithRights === 0
      ? "No venue is offered this channel yet: none holds a right to it. Grant one from the venue's setup (Venues, then the venue, then Rights, at channel level)."
      : null;
  }
  return s.fixtures === 0 ? 'Nothing is scheduled on this stream, so venues have nothing to pick from it. To offer it on its own, make it a Channel.' : null;
}
